import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { MapPin, Compass, Clock, Users, Sun, CloudRain, Utensils, Hotel, Train, ChevronRight } from 'lucide-react';

/**
 * Guide Index Page - /guide
 * Lists all available tourist guides
 */
export default function GuideIndexPage() {
  // Guide categories with their routes
  const guideCategories = [
    {
      title: "Découvrir Versailles",
      icon: Compass,
      guides: [
        { slug: "visiter-versailles", title: "Visiter Versailles : Guide Complet", description: "Tout savoir pour organiser votre visite" },
        { slug: "que-faire-a-versailles", title: "Que faire à Versailles ?", description: "Les meilleures activités et visites" },
        { slug: "versailles-en-1-jour", title: "Versailles en 1 jour", description: "Itinéraire optimisé depuis Paris" },
        { slug: "versailles-en-2-jours", title: "Versailles en 2 jours", description: "Pour profiter pleinement" },
      ]
    },
    {
      title: "Lieux à visiter",
      icon: MapPin,
      guides: [
        { slug: "chateau-de-versailles", title: "Château de Versailles", description: "Le palais de Louis XIV" },
        { slug: "jardins-de-versailles", title: "Jardins de Versailles", description: "Les jardins de Le Nôtre" },
        { slug: "galerie-des-glaces", title: "Galerie des Glaces", description: "Le joyau du château" },
        { slug: "grand-trianon", title: "Grand Trianon", description: "Le palais de marbre rose" },
        { slug: "petit-trianon", title: "Petit Trianon", description: "Le domaine de Marie-Antoinette" },
        { slug: "hameau-de-la-reine", title: "Hameau de la Reine", description: "Le village de la Reine" },
      ]
    },
    {
      title: "Conseils pratiques",
      icon: Clock,
      guides: [
        { slug: "comment-venir-a-versailles", title: "Comment venir à Versailles", description: "Transports depuis Paris" },
        { slug: "visiter-versailles-depuis-paris", title: "Depuis Paris", description: "Guide pratique d'accès" },
      ]
    },
    {
      title: "Situations spéciales",
      icon: Users,
      guides: [
        { slug: "versailles-avec-des-enfants", title: "Versailles avec des enfants", description: "Conseils pour les familles" },
        { slug: "versailles-quand-il-pleut", title: "Versailles quand il pleut", description: "Que faire par mauvais temps" },
      ]
    },
    {
      title: "Où manger & dormir",
      icon: Utensils,
      guides: [
        { slug: "ou-manger-a-versailles", title: "Où manger à Versailles", description: "Restaurants et bonnes adresses" },
        { slug: "ou-dormir-a-versailles", title: "Où dormir à Versailles", description: "Hôtels et hébergements" },
      ]
    }
  ];

  // Multilingual versions available
  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      <SEOHead 
        title="Guide Touristique Versailles | VersaillesPlus"
        description="Guide complet pour visiter Versailles : château, jardins, Trianon. Conseils pratiques, itinéraires, restaurants et hébergements."
        canonical="https://versaillesplus.fr/guide"
      />
      
      {/* JSON-LD for Guide page */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Guide Touristique Versailles",
        "description": "Guide complet pour visiter Versailles",
        "url": "https://versaillesplus.fr/guide",
        "publisher": {
          "@type": "Organization",
          "name": "VersaillesPlus"
        }
      })}} />
      
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero */}
        <div className="bg-gradient-to-br from-red-800 to-red-900 rounded-xl p-8 md:p-12 text-white mb-12">
          <nav className="flex items-center gap-2 text-sm text-red-200 mb-4">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="h-4 w-4" />
            <span>Guide</span>
          </nav>
          
          <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Guide Touristique Versailles
          </h1>
          <p className="font-lora text-lg md:text-xl text-red-100 max-w-2xl leading-relaxed">
            Tout ce qu'il faut savoir pour visiter Versailles : le château, les jardins, 
            les Trianons, et la ville. Conseils pratiques, itinéraires recommandés, 
            bonnes adresses.
          </p>
          
          {/* Language switcher */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <span className="text-red-200 text-sm">Disponible en :</span>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm">🇫🇷 Français</span>
              {languages.map((lang) => (
                <Link 
                  key={lang.code}
                  to={`/${lang.code}/guide`}
                  className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded-full text-sm transition-colors"
                >
                  {lang.flag} {lang.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Guide Categories Grid */}
        <div className="space-y-12">
          {guideCategories.map((category, idx) => (
            <section key={idx}>
              <h2 className="font-playfair text-2xl font-bold text-stone-900 mb-6 flex items-center gap-3">
                <category.icon className="h-6 w-6 text-red-700" />
                {category.title}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.guides.map((guide, gIdx) => (
                  <Link
                    key={gIdx}
                    to={`/${guide.slug}`}
                    className="bg-white rounded-lg border border-stone-200 p-5 hover:border-red-300 hover:shadow-md transition-all group"
                  >
                    <h3 className="font-playfair text-lg font-semibold text-stone-900 mb-2 group-hover:text-red-700 transition-colors">
                      {guide.title}
                    </h3>
                    <p className="font-inter text-sm text-stone-600">
                      {guide.description}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-3 text-red-700 font-inter text-sm font-medium">
                      Lire le guide
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Link to Archives */}
        <section className="mt-16 bg-stone-100 rounded-xl p-8 text-center">
          <h2 className="font-playfair text-2xl font-bold text-stone-900 mb-3">
            Explorez aussi nos archives
          </h2>
          <p className="font-inter text-stone-600 mb-6 max-w-lg mx-auto">
            Depuis 2007, VersaillesPlus chronique l'actualité versaillaise. 
            Découvrez près de 3 000 articles dans nos archives.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/archives"
              className="px-6 py-3 bg-red-700 text-white rounded-lg font-inter font-medium hover:bg-red-800 transition-colors"
            >
              Voir les archives
            </Link>
            <Link 
              to="/numeros"
              className="px-6 py-3 bg-white text-stone-700 rounded-lg font-inter font-medium hover:bg-stone-50 border border-stone-200 transition-colors"
            >
              Numéros PDF
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
